import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'
import { avatarColor } from '../lib/avatar'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface Attendee {
  email: string
  name: string | null
  self: boolean
  organizer: boolean
}

interface CalendarUpcomingEvent {
  eventId: string
  seriesId?: string | null
  title: string
  startTime: string | null
  endTime: string | null
  status?: string
  meetingUrl: string | null
  botScheduled: boolean
  isPast?: boolean
  attendees?: Attendee[]
}

interface ConversationItem {
  type: 'meeting' | 'email'
  title?: string
  subject?: string
  date: string | null
  documentId?: string
  from?: string
}

interface AttendeeContext {
  person: { email: string; name?: string }
  conversations: ConversationItem[]
  needsReconnect?: boolean
}

interface CalendarStatus {
  connected: boolean
  calendarId?: string
  accountEmail?: string | null
  autoRecord?: boolean
  upcoming: CalendarUpcomingEvent[]
}

interface BotJob {
  id: string
  botId: string
  eventId?: string
  documentId?: string
  status: string
  statusDetail?: string
  updatedAt?: unknown
}

const TERMINAL_BOT_STATUSES = ['completed', 'failed', 'bot_failed', 'left']

/** botJobのstatusをバッジ表示にマッピング */
function botStatusBadge(status: string): { className: string; label: string; pulse?: boolean } | null {
  switch (status) {
    case 'scheduled':
    case 'waiting':
      return { className: 'badge', label: '参加待ち' }
    case 'in_call':
    case 'in_progress':
      return { className: 'badge badge-warning', label: '録音中', pulse: true }
    case 'processing':
      return { className: 'badge badge-info', label: '処理中' }
    case 'completed':
      return { className: 'badge badge-success', label: '完了' }
    case 'failed':
    case 'bot_failed':
      return { className: 'badge badge-danger', label: '失敗' }
    default:
      return null
  }
}

function BotStatusBadge({ status }: { status: string }) {
  const badge = botStatusBadge(status)
  if (!badge) return null
  return (
    <span className={badge.className}>
      {badge.pulse && (
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'currentColor',
            marginRight: 4,
            animation: 'bp-bot-pulse 1.2s ease-in-out infinite'
          }}
        />
      )}
      {badge.label}
      {badge.pulse && (
        <style>{'@keyframes bp-bot-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }'}</style>
      )}
    </span>
  )
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function dayLabel(iso: string | null): string {
  if (!iso) return '日時未定'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '日時未定'
  const today = startOfDay(new Date())
  const target = startOfDay(d)
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (diffDays === 0) return '今日'
  if (diffDays === 1) return '明日'
  return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })
}

function formatTime(iso: string | null): string {
  if (!iso) return '--:--'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
}

// ── アバター（Circleback風・決定的カラーは lib/avatar を共用） ──
function attendeeInitial(a: Attendee): string {
  const base = (a.name || a.email.split('@')[0] || '').trim()
  return (base.charAt(0) || '?').toUpperCase()
}

/** 表示する代表出席者: self以外 → organizer → 先頭 */
function primaryAttendee(event: CalendarUpcomingEvent): Attendee | null {
  const list = event.attendees ?? []
  return list.find((a) => !a.self) || list.find((a) => a.organizer) || list[0] || null
}

function EventAvatar({ event }: { event: CalendarUpcomingEvent }) {
  const a = primaryAttendee(event)
  const label = a ? attendeeInitial(a) : ((event.title || '会').trim().charAt(0) || '会').toUpperCase()
  const key = a ? a.email : event.title || ''
  return (
    <span className="avatar" style={{ backgroundColor: avatarColor(key), color: '#fff', border: 'none' }}>
      {label}
    </span>
  )
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
}

/** 右側コンテキストパネル（Circlebackの「Details」相当） */
function ContextPanel({
  event,
  context,
  loading,
  onClose
}: {
  event: CalendarUpcomingEvent
  context: AttendeeContext | undefined
  loading: boolean
  onClose: () => void
}) {
  const people = (event.attendees ?? []).filter((a) => !a.self)
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">詳細</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="閉じる">
          ✕
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{event.title || '（無題の予定）'}</span>
        <span className="text-xs text-secondary">
          {dayLabel(event.startTime)} {formatTime(event.startTime)} - {formatTime(event.endTime)}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold text-secondary">参加者</h4>
        {people.length === 0 ? (
          <span className="text-xs text-secondary">参加者情報がありません</span>
        ) : (
          people.map((a) => (
            <Link
              key={a.email}
              to={`/people/${encodeURIComponent(a.email)}`}
              className="flex items-center gap-2 min-w-0"
              style={{ textDecoration: 'none', color: 'inherit' }}
              title="人物プロフィールを表示"
            >
              <span
                className="avatar avatar-sm"
                style={{ backgroundColor: avatarColor(a.email), color: '#fff', border: 'none' }}
              >
                {attendeeInitial(a)}
              </span>
              <div className="flex flex-col min-w-0">
                {a.name && <span className="text-sm truncate">{a.name}</span>}
                <span className="text-xs text-secondary truncate">{a.email}</span>
              </div>
            </Link>
          ))
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold text-secondary">最近のやり取り</h4>
        {loading ? (
          <div className="flex items-center gap-2">
            <span className="spinner sm" />
            <span className="text-xs text-secondary">読み込み中...</span>
          </div>
        ) : people.length === 0 && (event.attendees ?? []).length === 0 ? (
          <span className="text-xs text-secondary">参加者情報がないため履歴を表示できません</span>
        ) : !context ? (
          <span className="text-xs text-secondary">情報を取得できませんでした</span>
        ) : (
          <>
            {context.needsReconnect && (
              <span className="text-xs text-secondary">カレンダーを再接続するとメール履歴が表示されます</span>
            )}
            {context.conversations.length === 0 ? (
              <span className="text-xs text-secondary">最近のやり取りはありません</span>
            ) : (
              context.conversations.map((c, i) => (
                <div key={i} className="flex items-start gap-2 min-w-0">
                  <span style={{ fontSize: 14, lineHeight: '20px' }}>{c.type === 'email' ? '📧' : '📅'}</span>
                  <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                    {c.type === 'meeting' && c.documentId ? (
                      <Link to={`/documents/${c.documentId}`} className="text-sm truncate">
                        {c.title || '会議'}
                      </Link>
                    ) : (
                      <span className="text-sm truncate">
                        {c.type === 'email' ? c.subject || '（件名なし）' : c.title || '会議'}
                      </span>
                    )}
                    <span className="text-xs text-secondary">{shortDate(c.date)}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  )
}

function groupEventsByDay(events: CalendarUpcomingEvent[]): { label: string; events: CalendarUpcomingEvent[] }[] {
  const sorted = [...events].sort((a, b) => {
    const ta = a.startTime ? new Date(a.startTime).getTime() : Infinity
    const tb = b.startTime ? new Date(b.startTime).getTime() : Infinity
    return ta - tb
  })
  const groups: { label: string; events: CalendarUpcomingEvent[] }[] = []
  for (const event of sorted) {
    const label = dayLabel(event.startTime)
    const last = groups[groups.length - 1]
    if (last && last.label === label) {
      last.events.push(event)
    } else {
      groups.push({ label, events: [event] })
    }
  }
  return groups
}

function pastDayLabel(iso: string | null): string {
  if (!iso) return '日時未定'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '日時未定'
  return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })
}

function groupPastEventsByDay(events: CalendarUpcomingEvent[]): { label: string; events: CalendarUpcomingEvent[] }[] {
  const sorted = [...events].sort((a, b) => {
    const ta = a.startTime ? new Date(a.startTime).getTime() : -Infinity
    const tb = b.startTime ? new Date(b.startTime).getTime() : -Infinity
    return tb - ta // 新しい順
  })
  const groups: { label: string; events: CalendarUpcomingEvent[] }[] = []
  for (const event of sorted) {
    const label = pastDayLabel(event.startTime)
    const last = groups[groups.length - 1]
    if (last && last.label === label) {
      last.events.push(event)
    } else {
      groups.push({ label, events: [event] })
    }
  }
  return groups
}

function eventDateSuffix(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date()
  const valid = !isNaN(d.getTime()) ? d : new Date()
  const y = valid.getFullYear()
  const m = String(valid.getMonth() + 1).padStart(2, '0')
  const day = String(valid.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

/** VTT/SRTのタイムスタンプ・カウンター行を除去してプレーンテキスト化 */
function stripTranscriptMarkup(raw: string): string {
  const lines = raw.replace(/\r\n?/g, '\n').split('\n')
  const out: string[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    if (/^WEBVTT/i.test(trimmed)) continue
    if (/^(NOTE|STYLE|REGION)\b/.test(trimmed)) continue
    if (/^\d+$/.test(trimmed)) continue // SRTカウンター
    if (/-->/.test(trimmed)) continue // タイムスタンプ行
    out.push(trimmed.replace(/<[^>]+>/g, ''))
  }
  return out.join('\n')
}

const MAX_AUDIO_SIZE = 500 * 1024 * 1024 // Storageルールの上限（25MB超はサーバー側で自動分割文字起こし）
const TEXT_EXTENSIONS = ['.txt', '.vtt', '.srt', '.md']

type RegisterPhase = 'idle' | 'creating' | 'uploading' | 'transcribing' | 'done' | 'error'

export function MeetingsPage() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [searchParams, setSearchParams] = useSearchParams()
  const calendarParam = searchParams.get('calendar')
  const calendarReason = searchParams.get('reason')

  const [status, setStatus] = useState<CalendarStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [togglingAuto, setTogglingAuto] = useState(false)
  const [togglingEvent, setTogglingEvent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recordNotice, setRecordNotice] = useState<string | null>(null)
  // 会議URL貼り付けによるBot派遣
  const [meetingUrlInput, setMeetingUrlInput] = useState('')

  // サイドバーの「Botを会議に招待」から ?focus=bot-url で入力欄にフォーカス
  useEffect(() => {
    if (searchParams.get('focus') === 'bot-url') {
      // モバイルではフォームがボトムシート内にあるため先にシートを開く
      if (window.matchMedia('(max-width: 767px)').matches) setShowBotSheet(true)
      setTimeout(() => document.getElementById('bot-url-input')?.focus(), 100)
      searchParams.delete('focus')
      setSearchParams(searchParams, { replace: true })
    }
    // マウント後の再ナビゲーション（会議予定ページ表示中に＋メニューから選択）にも反応する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [meetingTitleInput, setMeetingTitleInput] = useState('')
  const [dispatching, setDispatching] = useState(false)
  const [showBotSheet, setShowBotSheet] = useState(false)
  const [dispatchAlert, setDispatchAlert] = useState<{ type: 'success' | 'danger'; message: string; documentId?: string } | null>(null)

  // 過去の会議セクション
  const [showPast, setShowPast] = useState(false)

  // ── 右側コンテキストパネル（選択イベント + 出席者コンテキスト） ──
  const [selectedEvent, setSelectedEvent] = useState<CalendarUpcomingEvent | null>(null)
  const [contextCache, setContextCache] = useState<Record<string, AttendeeContext>>({})
  const [contextLoading, setContextLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // ファイル登録モーダル
  const [registerEvent, setRegisterEvent] = useState<CalendarUpcomingEvent | null>(null)
  const [registerFile, setRegisterFile] = useState<File | null>(null)
  const [registerPhase, setRegisterPhase] = useState<RegisterPhase>('idle')
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [registerDocId, setRegisterDocId] = useState<string | null>(null)

  const [calendarAlert, setCalendarAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(
    calendarParam === 'connected'
      ? { type: 'success', message: 'Googleカレンダーを接続しました。' }
      : calendarParam === 'error'
        ? { type: 'danger', message: `カレンダー接続に失敗しました${calendarReason ? `（${calendarReason}）` : ''}。もう一度お試しください。` }
        : null
  )

  useEffect(() => {
    if (calendarParam) {
      const next = new URLSearchParams(searchParams)
      next.delete('calendar')
      next.delete('reason')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  const fetchStatus = useCallback(async () => {
    if (!firebaseUser) return
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar/status')
      if (!res.ok) throw new Error(`会議予定の取得に失敗しました (${res.status})`)
      setStatus(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : '会議予定の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [firebaseUser, authFetch])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  // 選択イベントの代表出席者のコンテキストを取得（メールアドレス単位でキャッシュ）
  const selectedAttendee = selectedEvent ? primaryAttendee(selectedEvent) : null
  const selectedEmail = selectedAttendee?.email ?? null
  useEffect(() => {
    if (!selectedEmail || contextCache[selectedEmail]) return
    let cancelled = false
    setContextLoading(true)
    ;(async () => {
      try {
        const params = new URLSearchParams({ email: selectedEmail })
        if (selectedAttendee?.name) params.set('name', selectedAttendee.name)
        const res = await authFetch(`/v1/meeting-bots/attendee-context?${params.toString()}`)
        const data = await res.json().catch(() => null)
        if (!cancelled && res.ok && data) {
          setContextCache((c) => ({ ...c, [selectedEmail]: data as AttendeeContext }))
        }
      } catch {
        // ベストエフォート
      } finally {
        if (!cancelled) setContextLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail, authFetch])

  // ── ボットジョブのリアルタイムステータス（30秒ポーリング） ──
  const [botJobs, setBotJobs] = useState<BotJob[]>([])

  const fetchBotJobs = useCallback(async () => {
    if (!firebaseUser) return
    try {
      const res = await authFetch('/v1/meeting-bots')
      if (!res.ok) return
      const data = await res.json().catch(() => null)
      if (Array.isArray(data?.jobs)) setBotJobs(data.jobs as BotJob[])
    } catch {
      // ベストエフォート
    }
  }, [firebaseUser, authFetch])

  useEffect(() => {
    fetchBotJobs()
  }, [fetchBotJobs])

  const hasActiveJobs = botJobs.some((j) => !TERMINAL_BOT_STATUSES.includes(j.status))

  useEffect(() => {
    if (!hasActiveJobs) return
    const timer = setInterval(fetchBotJobs, 30_000)
    return () => clearInterval(timer)
  }, [hasActiveJobs, fetchBotJobs])

  /** eventIdに紐づく最新のbotJobを返す（一覧は作成日降順） */
  const findBotJob = useCallback(
    (eventId: string): BotJob | undefined => botJobs.find((j) => j.eventId === eventId),
    [botJobs]
  )

  const handleConnect = async () => {
    setConnecting(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar/oauth/start')
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `連携の開始に失敗しました (${res.status})`)
      if (!data?.authUrl) throw new Error('認証URLの取得に失敗しました')
      window.location.href = data.authUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : '連携の開始に失敗しました')
      setConnecting(false)
    }
  }

  const handleAutoRecordToggle = async (next: boolean) => {
    setTogglingAuto(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar', {
        method: 'PATCH',
        body: JSON.stringify({ autoRecord: next })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || `設定の変更に失敗しました (${res.status})`)
      }
      setStatus((s) => (s ? { ...s, autoRecord: next } : s))
    } catch (e) {
      setError(e instanceof Error ? e.message : '設定の変更に失敗しました')
    } finally {
      setTogglingAuto(false)
    }
  }

  const handleEventRecordToggle = async (event: CalendarUpcomingEvent, next: boolean) => {
    setTogglingEvent(event.eventId)
    setError(null)
    // 楽観的更新
    setStatus((s) =>
      s
        ? {
            ...s,
            upcoming: s.upcoming.map((e) =>
              e.eventId === event.eventId ? { ...e, botScheduled: next } : e
            )
          }
        : s
    )
    try {
      const res = await authFetch(`/v1/meeting-bots/calendar/events/${encodeURIComponent(event.eventId)}/record`, {
        method: 'POST',
        body: JSON.stringify({ record: next, seriesId: event.seriesId ?? undefined })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        throw new Error(data?.error || `記録設定の変更に失敗しました (${res.status})`)
      }
      // 開始済み・開始直前の会議は即時派遣にフォールバックされる
      if (data?.mode === 'immediate' && next) {
        setRecordNotice('Botが会議に参加します')
        setTimeout(() => setRecordNotice(null), 5000)
      }
    } catch (e) {
      // 失敗時はロールバック
      setStatus((s) =>
        s
          ? {
              ...s,
              upcoming: s.upcoming.map((ev) =>
                ev.eventId === event.eventId ? { ...ev, botScheduled: !next } : ev
              )
            }
          : s
      )
      setError(e instanceof Error ? e.message : '記録設定の変更に失敗しました')
    } finally {
      setTogglingEvent(null)
    }
  }

  const handleDispatchBot = async () => {
    const url = meetingUrlInput.trim()
    let parsed: URL
    try {
      parsed = new URL(url)
    } catch {
      setDispatchAlert({ type: 'danger', message: '有効なURLを入力してください' })
      return
    }
    if (parsed.protocol !== 'https:') {
      setDispatchAlert({ type: 'danger', message: 'https:// で始まる会議URLを入力してください' })
      return
    }
    setDispatching(true)
    setDispatchAlert(null)
    try {
      const title = meetingTitleInput.trim() || `会議 ${new Date().toLocaleDateString('ja-JP')}`
      const res = await authFetch('/v1/meeting-bots', {
        method: 'POST',
        body: JSON.stringify({
          provider: 'meetingbaas',
          meetingUrl: url,
          title
        })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `Botの派遣に失敗しました (${res.status})`)
      setDispatchAlert({
        type: 'success',
        message: 'Botを会議に派遣しました。会議終了後に議事録ドキュメントが更新されます。',
        documentId: data?.documentId
      })
      setMeetingUrlInput('')
      setMeetingTitleInput('')
    } catch (e) {
      setDispatchAlert({ type: 'danger', message: e instanceof Error ? e.message : 'Botの派遣に失敗しました' })
    } finally {
      setDispatching(false)
    }
  }

  const openRegisterModal = (event: CalendarUpcomingEvent) => {
    setRegisterEvent(event)
    setRegisterFile(null)
    setRegisterPhase('idle')
    setRegisterError(null)
    setRegisterDocId(null)
  }

  const closeRegisterModal = () => {
    if (registerPhase === 'creating' || registerPhase === 'uploading' || registerPhase === 'transcribing') return
    setRegisterEvent(null)
  }

  const isTextFile = (file: File) =>
    TEXT_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))

  const handleRegisterFileChange = (file: File | null) => {
    setRegisterError(null)
    if (file && !isTextFile(file) && file.size > MAX_AUDIO_SIZE) {
      setRegisterFile(null)
      setRegisterError(`ファイルサイズが大きすぎます（${(file.size / 1024 / 1024).toFixed(1)} MB）。音声・動画は500MBまでです。`)
      return
    }
    setRegisterFile(file)
  }

  const createDocumentApi = async (body: Record<string, unknown>): Promise<string> => {
    if (!user?.orgId) throw new Error('組織情報を取得できませんでした')
    const res = await authFetch(`/v1/organizations/${user.orgId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) throw new Error(data?.error || `ドキュメントの作成に失敗しました (${res.status})`)
    if (!data?.id) throw new Error('ドキュメントIDの取得に失敗しました')
    return data.id as string
  }

  const handleRegisterSubmit = async () => {
    if (!registerEvent || !registerFile || !firebaseUser || !user?.orgId) return
    const file = registerFile
    const title = `${registerEvent.title || '会議'}（${eventDateSuffix(registerEvent.startTime)}）`
    // 予定の参加者を議事録に引き継ぐ（話者推定の実名ラベルにも使われる）
    const participants = (registerEvent.attendees ?? [])
      .map((a) => a.name || a.email || '')
      .filter(Boolean)
    setRegisterError(null)
    try {
      if (isTextFile(file)) {
        // テキスト系: クライアントで読み込み → transcript付きでドキュメント作成
        setRegisterPhase('creating')
        const raw = await file.text()
        const text = /\.(vtt|srt)$/i.test(file.name) ? stripTranscriptMarkup(raw) : raw.trim()
        if (!text) throw new Error('ファイルからテキストを読み取れませんでした')
        const docId = await createDocumentApi({
          title,
          category: 'meeting',
          participants,
          transcript: { text, lang: 'ja', source: 'paste', durationSec: 0, chunks: [] }
        })
        setRegisterDocId(docId)
        setRegisterPhase('done')
      } else {
        // 音声・動画: ドキュメント作成 → Storageアップロード → 文字起こしリクエスト
        // （25MB超はサーバー側で自動的に圧縮・分割される）
        setRegisterPhase('creating')
        const docId = await createDocumentApi({ title, category: 'meeting', participants })
        setRegisterDocId(docId)

        setRegisterPhase('uploading')
        const { getStorage, ref, uploadBytes } = await import('firebase/storage')
        const storage = getStorage()
        const storagePath = `audio/${user.orgId}/${docId}/${Date.now()}_${file.name}`
        await uploadBytes(ref(storage, storagePath), file)

        setRegisterPhase('transcribing')
        const res = await authFetch(`/v1/organizations/${user.orgId}/documents/${docId}/transcribe`, {
          method: 'POST',
          body: JSON.stringify({ audioPath: storagePath, fileName: file.name, mimeType: file.type })
        })
        if (!res.ok) {
          const data = await res.json().catch(() => null)
          throw new Error(data?.error || `文字起こしの開始に失敗しました (${res.status})`)
        }
        setRegisterPhase('done')
      }
    } catch (e) {
      setRegisterPhase('error')
      setRegisterError(e instanceof Error ? e.message : 'ファイル登録に失敗しました')
    }
  }

  const todayText = new Date().toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  })

  const upcomingEvents = status?.connected ? status.upcoming.filter((e) => !e.isPast) : []
  const pastEvents = status?.connected ? status.upcoming.filter((e) => e.isPast) : []
  const groups = groupEventsByDay(upcomingEvents)
  const pastGroups = groupPastEventsByDay(pastEvents)

  // Bot派遣フォーム（PC: インラインカード / モバイル: FABから開くボトムシートで共用）
  const botDispatchForm = (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          className="form-input"
          style={{ flex: '2 1 240px' }}
          id="bot-url-input"
          placeholder="会議URLを貼り付け (Meet / Zoom / Teams)"
          value={meetingUrlInput}
          onChange={(e) => setMeetingUrlInput(e.target.value)}
          disabled={dispatching}
        />
        <input
          type="text"
          className="form-input"
          style={{ flex: '1 1 160px' }}
          placeholder="タイトル（任意）"
          value={meetingTitleInput}
          onChange={(e) => setMeetingTitleInput(e.target.value)}
          disabled={dispatching}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={dispatching || !meetingUrlInput.trim()}
          onClick={handleDispatchBot}
        >
          {dispatching && <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />}
          Botを参加させる
        </button>
      </div>
      {dispatchAlert && (
        <div className={`alert ${dispatchAlert.type === 'success' ? 'success' : 'danger'}`}>
          <span>
            {dispatchAlert.message}
            {dispatchAlert.documentId && (
              <>
                {' '}
                <Link to={`/documents/${dispatchAlert.documentId}`}>作成されたドキュメントを開く</Link>
              </>
            )}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDispatchAlert(null)}>
            閉じる
          </button>
        </div>
      )}
    </>
  )

  return (
    <Layout title="会議予定">
      <div
        className="flex gap-4"
        style={{
          width: '100%',
          minWidth: 0,
          maxWidth: selectedEvent && !isMobile ? 1060 : 720,
          margin: '0 auto',
          alignItems: 'flex-start'
        }}
      >
      <div className="flex flex-col gap-4" style={{ flex: 1, minWidth: 0 }}>
        {calendarAlert && (
          <div className={`alert ${calendarAlert.type === 'success' ? 'success' : 'danger'}`}>
            <span>{calendarAlert.message}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setCalendarAlert(null)}
            >
              閉じる
            </button>
          </div>
        )}

        {/* ── ヘッダー ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold">今日の会議</h2>
            <p className="text-secondary text-sm">{todayText}</p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && status?.connected && (
              <label className="toggle-wrapper">
                <span className="toggle">
                  <input
                    type="checkbox"
                    checked={status.autoRecord !== false}
                    disabled={togglingAuto}
                    onChange={(e) => handleAutoRecordToggle(e.target.checked)}
                  />
                  <span className="toggle-track"></span>
                  <span className="toggle-thumb"></span>
                </span>
                <span className="toggle-label text-sm">自動記録</span>
              </label>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={loading}
              onClick={() => { fetchStatus(); fetchBotJobs() }}
            >
              更新
            </button>
          </div>
        </div>

        {/* ── 会議URLを貼り付けてBotを派遣（モバイルではFAB→ボトムシート） ── */}
        {!isMobile && (
          <div className="card">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">新しい会議にBotを参加させる</h3>
              {botDispatchForm}
            </div>
          </div>
        )}
        {isMobile && dispatchAlert && (
          <div className={`alert ${dispatchAlert.type === 'success' ? 'success' : 'danger'}`}>
            <span>
              {dispatchAlert.message}
              {dispatchAlert.documentId && (
                <>
                  {' '}
                  <Link to={`/documents/${dispatchAlert.documentId}`}>作成されたドキュメントを開く</Link>
                </>
              )}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDispatchAlert(null)}>
              閉じる
            </button>
          </div>
        )}

        {error && (
          <div className="alert danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>{error}</span>
            <button className="btn btn-outline btn-sm" onClick={() => setError(null)} style={{ flexShrink: 0 }}>閉じる</button>
          </div>
        )}
        {recordNotice && <div className="alert success">{recordNotice}</div>}

        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center">
            <span className="spinner" />
            <span className="text-secondary text-sm">読み込み中...</span>
          </div>
        ) : !status?.connected ? (
          <div className="card">
            <div className="empty-state">
              <h3 className="text-lg font-semibold">カレンダーが未接続です</h3>
              <p className="text-secondary text-sm">
                Googleカレンダーを接続すると、今後の会議予定が表示され、議事録ボットの派遣を管理できます。
              </p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={connecting}
                onClick={handleConnect}
              >
                {connecting && <span className="spinner sm" />}
                Googleカレンダーを接続
              </button>
            </div>
          </div>
        ) : (
          <>
            {groups.length === 0 ? (
              <div className="card">
                <div className="empty-state">
                  <h3 className="text-lg font-semibold">今後の予定はありません</h3>
                  <p className="text-secondary text-sm">カレンダーに会議が追加されるとここに表示されます。</p>
                </div>
              </div>
            ) : (
              groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-secondary">{group.label}</h3>
              <div className="card" style={{ padding: 0 }}>
                {group.events.map((event, i) => (
                  <div
                    key={event.eventId}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                    style={{
                      cursor: 'pointer',
                      ...(i > 0 ? { borderTop: '1px solid var(--color-border-light)' } : {}),
                      ...(selectedEvent?.eventId === event.eventId ? { background: 'var(--color-surface-hover, rgba(0,0,0,0.03))' } : {})
                    }}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <EventAvatar event={event} />
                    <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{event.title || '（無題の予定）'}</span>
                        {(() => {
                          const job = findBotJob(event.eventId)
                          if (job) return <BotStatusBadge status={job.status} />
                          return event.botScheduled ? <span className="badge badge-success">録画予定</span> : null
                        })()}
                      </div>
                      <span className="text-xs text-secondary">
                        {formatTime(event.startTime)} - {formatTime(event.endTime)}
                        {!event.meetingUrl && ' ・ 会議URLなし'}
                      </span>
                    </div>
                    {/* 会議URLが無い予定はbot派遣できないためトグルを出さない（モバイルのノイズ削減） */}
                    {event.meetingUrl ? (
                      <label
                        className="toggle-wrapper shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="toggle">
                          <input
                            type="checkbox"
                            checked={event.botScheduled}
                            disabled={togglingEvent === event.eventId}
                            onChange={(e) => handleEventRecordToggle(event, e.target.checked)}
                          />
                          <span className="toggle-track"></span>
                          <span className="toggle-thumb"></span>
                        </span>
                        <span className="toggle-label text-sm">記録</span>
                      </label>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
              ))
            )}

            {/* ── 過去の会議（3日間） ── */}
            {pastGroups.length > 0 && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setShowPast((v) => !v)}
                >
                  {showPast ? '▼' : '▶'} 過去の会議（3日間）
                </button>
                {showPast &&
                  pastGroups.map((group) => (
                    <div key={group.label} className="flex flex-col gap-2">
                      <h3 className="text-sm font-semibold text-secondary">{group.label}</h3>
                      <div className="card" style={{ padding: 0 }}>
                        {group.events.map((event, i) => (
                          <div
                            key={event.eventId}
                            className="flex items-center justify-between gap-3 px-4 py-3"
                            style={{
                              cursor: 'pointer',
                              ...(i > 0 ? { borderTop: '1px solid var(--color-border-light)' } : {}),
                              ...(selectedEvent?.eventId === event.eventId ? { background: 'var(--color-surface-hover, rgba(0,0,0,0.03))' } : {})
                            }}
                            onClick={() => setSelectedEvent(event)}
                          >
                            <EventAvatar event={event} />
                            <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-medium truncate">{event.title || '（無題の予定）'}</span>
                                {(() => {
                                  const job = findBotJob(event.eventId)
                                  return job ? <BotStatusBadge status={job.status} /> : null
                                })()}
                              </div>
                              <span className="text-xs text-secondary">
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm shrink-0"
                              onClick={(e) => { e.stopPropagation(); openRegisterModal(event) }}
                            >
                              ファイル登録
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── 右側コンテキストパネル（デスクトップ: sticky右カラム） ── */}
      {selectedEvent && !isMobile && (
        <div
          className="card"
          style={{ width: 320, flexShrink: 0, position: 'sticky', top: 16 }}
        >
          <ContextPanel
            event={selectedEvent}
            context={selectedEmail ? contextCache[selectedEmail] : undefined}
            loading={contextLoading}
            onClose={() => setSelectedEvent(null)}
          />
        </div>
      )}
      </div>

      {/* ── コンテキストパネル（モバイル: 全画面オーバーレイ） ── */}
      {selectedEvent && isMobile && (
        <div className="modal-overlay" style={{ alignItems: 'stretch', padding: 0 }}>
          <div
            style={{
              background: 'var(--color-surface)',
              width: '100%',
              minHeight: '100%',
              overflowY: 'auto',
              padding: 16
            }}
          >
            <ContextPanel
              event={selectedEvent}
              context={selectedEmail ? contextCache[selectedEmail] : undefined}
              loading={contextLoading}
              onClose={() => setSelectedEvent(null)}
            />
          </div>
        </div>
      )}

      {/* ── モバイル: Bot派遣ボトムシート（下部タブ＋メニューの「Botを会議に招待」から開く） ── */}
      {isMobile && showBotSheet && (
        <div className="modal-overlay" onClick={() => setShowBotSheet(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-md font-semibold">新しい会議にBotを参加させる</h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowBotSheet(false)}>閉じる</button>
            </div>
            <div className="flex flex-col gap-2" style={{ padding: '4px 0 8px' }}>
              {botDispatchForm}
            </div>
          </div>
        </div>
      )}

      {/* ── ファイル登録モーダル ── */}
      {registerEvent && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ width: 480 }}>
            <div className="modal-header">
              <div>
                <h3 className="text-lg font-semibold text-primary">ファイル登録</h3>
                <p className="text-sm text-tertiary">
                  「{registerEvent.title || '会議'}（{eventDateSuffix(registerEvent.startTime)}）」として議事録ドキュメントを作成します。
                </p>
              </div>
            </div>

            <div className="modal-body">
              {registerPhase === 'done' ? (
                <div className="alert success">
                  <div className="alert-body">
                    <div className="alert-title">登録が完了しました</div>
                    <p className="text-sm text-secondary">
                      {registerDocId && (
                        <Link to={`/documents/${registerDocId}`}>ドキュメントを開く</Link>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">音声・動画 または 文字起こしテキスト</label>
                    <input
                      type="file"
                      className="form-input"
                      accept="audio/*,video/mp4,video/*,.mp3,.wav,.m4a,.mp4,.mov,.webm,.txt,.vtt,.srt,.md"
                      disabled={registerPhase !== 'idle' && registerPhase !== 'error'}
                      onChange={(e) => handleRegisterFileChange(e.target.files?.[0] ?? null)}
                    />
                    <p className="form-hint">
                      音声・動画は自動で文字起こしされます（25MB超は自動分割・話者は参加者名で推定）。テキスト（.txt / .vtt / .srt / .md）はそのまま文字起こしとして登録されます。
                    </p>
                  </div>

                  {registerFile && (
                    <p className="text-sm text-secondary">
                      {registerFile.name}（{(registerFile.size / 1024 / 1024).toFixed(2)} MB）
                      {isTextFile(registerFile) ? ' ・ テキストとして登録' : ' ・ 文字起こしを実行'}
                    </p>
                  )}

                  {(registerPhase === 'creating' || registerPhase === 'uploading' || registerPhase === 'transcribing') && (
                    <div className="flex items-center gap-2">
                      <span className="spinner sm" />
                      <span className="text-sm text-secondary">
                        {registerPhase === 'creating' && 'ドキュメント作成中...'}
                        {registerPhase === 'uploading' && 'アップロード中...'}
                        {registerPhase === 'transcribing' && '文字起こし中...'}
                      </span>
                    </div>
                  )}

                  {registerError && <div className="alert danger">{registerError}</div>}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                disabled={registerPhase === 'creating' || registerPhase === 'uploading' || registerPhase === 'transcribing'}
                onClick={closeRegisterModal}
              >
                {registerPhase === 'done' ? '閉じる' : 'キャンセル'}
              </button>
              {registerPhase !== 'done' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    !registerFile ||
                    registerPhase === 'creating' ||
                    registerPhase === 'uploading' ||
                    registerPhase === 'transcribing'
                  }
                  onClick={handleRegisterSubmit}
                >
                  登録する
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
